import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Volume2, SkipForward, Smartphone, Wallet, DollarSign, Link2, Coins, ShieldCheck, PartyPopper } from "lucide-react";

const BASE = "/onboarding-video";

// Slides — captions mirror the voiceover exactly (see scripts/gen_onboarding_audio.py).
const SLIDES = [
  {
    audio: `${BASE}/01-welcome.mp3`, icon: PartyPopper, accent: "#4060FF",
    tag: "Welcome", title: "Getting started, in 2 minutes",
    caption: "Welcome to BotChainCP. I'll walk you through exactly how to get set up — from creating your wallet to earning your first daily rewards. Twelve simple steps, in four easy stages.",
    bullets: ["12 steps · 4 stages", "Up and running today"],
  },
  {
    audio: `${BASE}/02-wallet.mp3`, icon: Wallet, accent: "#7C3AED",
    tag: "Stage A", title: "Set up your TP Wallet",
    caption: "Everything runs through TP Wallet (TokenPocket). Install it, create a new wallet, and write your 12-word seed phrase on paper — keep it offline and private. Then add the BOT Chain network.",
    bullets: ["Install TP Wallet", "Back up your 12 words (offline!)", "Add BOT Chain network"],
  },
  {
    audio: `${BASE}/03-fund.mp3`, icon: DollarSign, accent: "#00863A",
    tag: "Stage B", title: "Fund your wallet",
    caption: "Add a few dollars of BNB for gas. Move the USDT you plan to commit into your wallet and bridge it to BOT Chain. Then add USDT and CA as custom tokens so balances display correctly.",
    bullets: ["A little BNB for gas", "Bring in USDT → bridge to BOT Chain", "Add USDT & CA tokens"],
  },
  {
    audio: `${BASE}/04-connect.mp3`, icon: Link2, accent: "#4060FF",
    tag: "Stage C", title: "Connect to CaryPact",
    caption: "Open your sponsor's invitation (the button carries your code automatically). Register and sign in with your wallet, then commit your USDT to hashrate — the moment your daily CA mining begins.",
    bullets: ["Open your sponsor invite", "Register & sign in", "Buy hashrate → mining starts"],
  },
  {
    audio: `${BASE}/05-earn.mp3`, icon: Coins, accent: "#F59E0B",
    tag: "Stage D", title: "Earn, stake & cash out",
    caption: "Your CA rewards accrue daily — claim them from the dashboard. Stake CA for extra yield (flexible or fixed-term). Whenever you want cash, swap CA → BOT → USDT, bridge back, and off-ramp.",
    bullets: ["Claim daily CA rewards", "Stake for extra yield", "Cash out: CA → BOT → USDT"],
  },
  {
    audio: `${BASE}/06-approval.mp3`, icon: ShieldCheck, accent: "#00863A",
    tag: "Approval", title: "Get approved, unlock everything",
    caption: "Finish all 12 steps and tap 'request approval'. Your sponsor approves first, then an admin confirms. The moment you're approved, your full console unlocks — wallets, forecasts and your team.",
    bullets: ["Sponsor approves →", "Admin confirms →", "Full console unlocks"],
  },
  {
    audio: `${BASE}/07-outro.mp3`, icon: Smartphone, accent: "#7C3AED",
    tag: "You're set", title: "Welcome to the team",
    caption: "That's it — you're ready. If you ever get stuck, message your admin right from the chat bubble. Now let's get you started.",
    bullets: ["Stuck? Use the chat bubble", "Let's go 🚀"],
  },
];

export const OnboardingVideo = ({ compact = false }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const slide = SLIDES[idx];
  const Icon = slide.icon;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.src = slide.audio;
    setProgress(0);
    if (playing) { a.play().catch(() => setPlaying(false)); }
  }, [idx]); // eslint-disable-line

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const restart = () => { setIdx(0); setProgress(0); const a = audioRef.current; if (a) { a.currentTime = 0; a.play().then(() => setPlaying(true)).catch(() => {}); } };
  const next = () => { if (idx < SLIDES.length - 1) setIdx(idx + 1); else { setPlaying(false); setProgress(0); } };

  const onEnded = () => { if (idx < SLIDES.length - 1) setIdx(idx + 1); else { setPlaying(false); setIdx(0); setProgress(0); } };
  const onTime = () => { const a = audioRef.current; if (a && a.duration) setProgress((a.currentTime / a.duration) * 100); };

  return (
    <div className="rounded-2xl overflow-hidden fade-up" style={{ border: "1px solid rgba(15,18,38,0.08)", boxShadow: "0 20px 60px -20px rgba(15,18,38,0.25)" }} data-testid="onboarding-video">
      {/* Stage */}
      <div className="relative aspect-video flex flex-col justify-center px-8 md:px-14 transition-colors duration-500"
        style={{ background: `linear-gradient(135deg, ${slide.accent}14, #0F122608), #FbFcFf` }}>
        <div className="absolute top-4 left-5 flex items-center gap-2 text-xs font-mono text-ink-3">
          <span className="inline-flex items-center gap-1"><Volume2 size={12} /> BotChainCP</span>
          <span>·</span><span>Onboarding</span>
        </div>
        <div className="absolute top-4 right-5 text-xs font-mono font-semibold" style={{ color: slide.accent }}>{idx + 1} / {SLIDES.length}</div>

        <div key={idx} className="fade-up">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${slide.accent}1a` }}>
            <Icon size={26} style={{ color: slide.accent }} />
          </div>
          <div className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: slide.accent }}>{slide.tag}</div>
          <h3 className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-ink mb-4">{slide.title}</h3>
          <div className="flex flex-wrap gap-2">
            {slide.bullets.map((b, i) => (
              <span key={i} className="text-xs md:text-sm px-3 py-1.5 rounded-full font-medium" style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${slide.accent}30`, color: "#0F1226" }}>{b}</span>
            ))}
          </div>
        </div>

        {/* Caption bar */}
        {!compact && (
          <div className="absolute bottom-3 left-0 right-0 px-6">
            <div className="mx-auto max-w-2xl text-center text-[13px] leading-snug text-ink-2 rounded-lg px-4 py-2" style={{ background: "rgba(255,255,255,0.72)", backdropFilter: "blur(6px)" }} data-testid="video-caption">
              {slide.caption}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: "#0F1226" }}>
        <button onClick={toggle} className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform hover:scale-105" style={{ background: slide.accent, color: "#fff" }} data-testid="video-play">
          {playing ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
        </button>
        <div className="flex-1">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: slide.accent }} />
          </div>
          <div className="flex justify-between mt-1.5">
            {SLIDES.map((s, i) => (
              <button key={i} onClick={() => setIdx(i)} className="h-1 rounded-full transition-all" title={s.title}
                style={{ width: `${100 / SLIDES.length - 1.5}%`, background: i <= idx ? slide.accent : "rgba(255,255,255,0.2)" }} data-testid={`video-chapter-${i}`} />
            ))}
          </div>
        </div>
        <button onClick={next} className="text-white/70 hover:text-white transition-colors shrink-0" title="Next" data-testid="video-next"><SkipForward size={18} /></button>
        <button onClick={restart} className="text-white/70 hover:text-white transition-colors shrink-0" title="Replay from start" data-testid="video-replay"><RotateCcw size={17} /></button>
      </div>
      <audio ref={audioRef} onEnded={onEnded} onTimeUpdate={onTime} preload="none" />
    </div>
  );
};

export default OnboardingVideo;
